import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger
} from '@bliks-io/ui';

export interface SelectOption {
  value: string;
  /** Rendered in the dropdown item and the trigger — may be a badge/pill. */
  label: React.ReactNode;
  /**
   * Optional leading icon, rendered before the label in the item and trigger.
   * Kept separate from `label` so a string label still matches `searchable`.
   */
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface SelectOptionGroup {
  /** Group heading — must stay a string (rendered as the Combobox heading). */
  label: string;
  options: SelectOption[];
}

export interface FormFieldSelectProps {
  /**
   * Field label
   */
  label?: string;

  /**
   * Field description/helper text
   */
  description?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Mark field as required
   */
  required?: boolean;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Selected value
   */
  value?: string;

  /**
   * Change handler
   */
  onValueChange?: (value: string | undefined) => void;

  /**
   * Select options (simple list)
   */
  options?: SelectOption[];

  /**
   * Select options (grouped)
   */
  optionGroups?: SelectOptionGroup[];

  /**
   * Whether the select is disabled
   */
  disabled?: boolean;

  /**
   * Field ID
   */
  id?: string;

  /**
   * Field name
   */
  name?: string;

  /**
   * Field orientation
   */
  orientation?: 'vertical' | 'horizontal' | 'responsive';

  /**
   * Additional class for Field wrapper
   */
  fieldClassName?: string;

  /**
   * Additional class for Trigger
   */
  className?: string;

  /**
   * Allow clearing the selection
   */
  allowClear?: boolean;

  /**
   * Message to show when no results found
   */
  emptyMessage?: string;

  /**
   * Render a search input in the dropdown and filter options client-side.
   * Only options with plain-string labels are matched, so don't combine with
   * badge/pill labels.
   */
  searchable?: boolean;

  /**
   * Placeholder for the search input (only with `searchable`)
   */
  searchPlaceholder?: string;
}

function optionMatches(option: SelectOption, lowerQuery: string): boolean {
  return typeof option.label === 'string' && option.label.toLowerCase().includes(lowerQuery);
}

/**
 * Pairs an optional leading icon with the label; falls back to the bare label.
 * The label gets its own `truncate` span (and the row `min-w-0`) so a long
 * value still ellipsizes inside the trigger's `truncate` box — a nested flex
 * child won't inherit the ellipsis otherwise.
 */
function renderOptionContent(option: Pick<SelectOption, 'icon' | 'label'>): React.ReactNode {
  if (!option.icon) return option.label;
  return (
    <span className="flex items-center gap-2 min-w-0">
      {option.icon}
      <span className="truncate">{option.label}</span>
    </span>
  );
}

function filterOptionGroups(
  groups: SelectOptionGroup[] | undefined,
  lowerQuery: string
): SelectOptionGroup[] | undefined {
  return groups
    ?.map((group) => ({
      ...group,
      options: group.options.filter((option) => optionMatches(option, lowerQuery))
    }))
    .filter((group) => group.options.length > 0);
}

/**
 * Reusable form field component combining Field + Combobox
 * Can be used standalone or wrapped by RHF adapter components
 */
export const FormFieldSelect = React.forwardRef<HTMLButtonElement, FormFieldSelectProps>(
  (
    {
      label,
      description,
      error,
      required = false,
      placeholder,
      value,
      onValueChange,
      options,
      optionGroups,
      disabled = false,
      id,
      name,
      orientation = 'vertical',
      fieldClassName,
      className,
      allowClear = false,
      emptyMessage,
      searchable = false,
      searchPlaceholder
    },
    ref
  ) => {
    const { t } = useTranslation();
    const fieldId = id || name;
    const hasError = !!error;

    // Use translation for empty message if not provided
    const defaultEmptyMessage = emptyMessage ?? t('common.noOptionsAvailable');

    const [searchQuery, setSearchQuery] = React.useState('');

    // The Combobox primitive disables cmdk's automatic filtering, so the
    // search filter is applied here over the option data. Most selects are
    // non-searchable, so the no-query path must return the inputs untouched.
    const isSearching = searchable && !!searchQuery;

    const visibleOptions = React.useMemo(() => {
      if (!isSearching) return options;
      const q = searchQuery.toLowerCase();
      return options?.filter((option) => optionMatches(option, q));
    }, [options, isSearching, searchQuery]);

    const visibleOptionGroups = React.useMemo(() => {
      if (!isSearching) return optionGroups;
      return filterOptionGroups(optionGroups, searchQuery.toLowerCase());
    }, [optionGroups, isSearching, searchQuery]);

    // Handle value change - convert to string | undefined
    const handleValueChange = React.useCallback(
      (newValue: string | string[] | undefined) => {
        if (typeof newValue === 'string') {
          onValueChange?.(newValue);
        } else {
          onValueChange?.(undefined);
        }
      },
      [onValueChange]
    );

    // Get selected label for display
    const selectedLabel = React.useMemo(() => {
      if (!value) return null;

      if (optionGroups) {
        for (const group of optionGroups) {
          const option = group.options.find((opt) => opt.value === value);
          if (option) return renderOptionContent(option);
        }
      }

      if (options) {
        const option = options.find((opt) => opt.value === value);
        if (option) return renderOptionContent(option);
      }

      return null;
    }, [value, options, optionGroups]);

    return (
      <Field orientation={orientation} className={fieldClassName} data-invalid={hasError}>
        {label && (
          <FieldLabel htmlFor={fieldId}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </FieldLabel>
        )}
        <FieldContent>
          {description && <FieldDescription>{description}</FieldDescription>}
          <Combobox value={value} onValueChange={handleValueChange} disabled={disabled}>
            <ComboboxTrigger
              ref={ref}
              id={fieldId}
              placeholder={placeholder}
              allowClear={allowClear}
              className={className}
            >
              {selectedLabel}
            </ComboboxTrigger>
            <ComboboxContent>
              {searchable && (
                <ComboboxInput
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
              )}
              <ComboboxList>
                <ComboboxEmpty>{defaultEmptyMessage}</ComboboxEmpty>
                {visibleOptionGroups
                  ? visibleOptionGroups.map((group) => (
                      <ComboboxGroup key={group.label} heading={group.label}>
                        {group.options.map((option) => (
                          <ComboboxItem
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                          >
                            {renderOptionContent(option)}
                          </ComboboxItem>
                        ))}
                      </ComboboxGroup>
                    ))
                  : visibleOptions?.map((option) => (
                      <ComboboxItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                      >
                        {renderOptionContent(option)}
                      </ComboboxItem>
                    ))}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
          {error && <FieldError>{error}</FieldError>}
        </FieldContent>
      </Field>
    );
  }
);

FormFieldSelect.displayName = 'FormFieldSelect';
